import { Injectable, computed, effect, signal } from '@angular/core';
import { CartItem } from './cart.models';

const CART_STORAGE_KEY = 'mh_cart_v1';

@Injectable({ providedIn: 'root' })
export class CartService {
  private readonly _items = signal<CartItem[]>(this.loadFromStorage());

  constructor() {
    // Persiste no localStorage a cada mudança do signal
    effect(() => {
      this.saveToStorage(this._items());
    });
  }

  readonly items = this._items.asReadonly();

  readonly count = computed(() =>
    this._items().reduce((acc, i) => acc + i.quantity, 0)
  );

  readonly total = computed(() =>
    this._items().reduce((acc, i) => {
      const extras = i.adicionais.reduce((a, x) => a + x.price, 0);
      return acc + (i.unitPrice + extras) * i.quantity;
    }, 0)
  );

  add(item: CartItem): void {
    this._items.update((items) => {
      const idx = items.findIndex((i) => i.productId === item.productId);
      if (idx >= 0) {
        return items.map((i, index) =>
          index === idx ? { ...i, quantity: i.quantity + item.quantity } : i
        );
      }
      return [...items, item];
    });
  }

  remove(productId: string): void {
    this._items.update((items) => items.filter((i) => i.productId !== productId));
  }

  updateQuantity(productId: string, quantity: number): void {
    if (quantity <= 0) {
      this.remove(productId);
      return;
    }
    this._items.update((items) =>
      items.map((i) => (i.productId === productId ? { ...i, quantity } : i))
    );
  }

  clear(): void {
    this._items.set([]);
  }

  private loadFromStorage(): CartItem[] {
    try {
      if (typeof localStorage === 'undefined') return [];
      const raw = localStorage.getItem(CART_STORAGE_KEY);
      return raw ? (JSON.parse(raw) as CartItem[]) : [];
    } catch {
      return [];
    }
  }

  private saveToStorage(items: CartItem[]): void {
    try {
      if (typeof localStorage === 'undefined') return;
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
    } catch { /* quota exceeded ou modo privado */ }
  }
}
